import type { Heading, Root, Yaml } from 'mdast'
import { toString } from 'mdast-util-to-string'
import remarkDirective from 'remark-directive'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import { unified } from 'unified'
import { visit } from 'unist-util-visit'
import { parse as parseYaml } from 'yaml'
import { getCatalogVariable, suggestCatalogVariable } from './catalog.ts'
import {
  ANAMNESIS_DOCUMENT_TYPES,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS,
  REQUIRED_CONSENT_FORM_BLOCKS,
  type DocumentType,
} from './dialect.ts'
import { remarkLegal } from './remark-legal.ts'
import type {
  AnamnesisData,
  DocumentMeta,
  LintDiagnostic,
  ParsedDocument,
  Variables,
} from './types.ts'
import { extractVariables } from './variables.ts'

/**
 * Lê o Markdown e devolve a árvore já no formato do documento, os metadados do front matter,
 * as variáveis usadas e os avisos de lint. Não lança: problema de escrita vira aviso.
 */
export function parseLegal(
  markdown: string,
  options: {
    variables?: Variables
    numbering?: boolean
    /** Confere cada `{{variável}}` contra o catálogo (`catalog.ts`): caminho fora dele é erro. */
    catalog?: boolean
    /**
     * As respostas que `::anamnesis` imprime (P-DOC-03), ou a ficha em branco (`mode: 'blank'`,
     * RN-ANA-24); sem nenhum dos dois, o bloco imprime `empty` e avisa.
     */
    anamnesis?: AnamnesisData | null
  } = {},
): ParsedDocument {
  const diagnostics: LintDiagnostic[] = []
  const processor = unified()
    .use(remarkParse)
    .use(remarkFrontmatter, ['yaml'])
    .use(remarkGfm)
    .use(remarkDirective)
    .use(remarkLegal, {
      variables: options.variables,
      numbering: options.numbering,
      diagnostics,
      anamnesis: options.anamnesis,
    })

  const tree = processor.runSync(processor.parse(markdown)) as Root

  // Front matter → meta (e sai da árvore)
  const meta: DocumentMeta = { type: 'other' }
  const yaml = tree.children.find((c): c is Yaml => c.type === 'yaml')
  if (yaml) {
    try {
      const data = parseYaml(yaml.value) as Record<string, unknown> | null
      if (data && typeof data === 'object') {
        Object.assign(meta, data)
        if (typeof data.type === 'string' && DOCUMENT_TYPES.includes(data.type as DocumentType))
          meta.type = data.type as DocumentType
        else if (data.type !== undefined) {
          diagnostics.push({
            severity: 'warning',
            code: 'invalid_front_matter',
            message: `O tipo “${String(data.type)}” não existe. Use: ${DOCUMENT_TYPES.join(', ')}.`,
          })
          meta.type = 'other'
        }
        if (data.updatedAt instanceof Date)
          meta.updatedAt = data.updatedAt.toISOString().slice(0, 10)
      }
    } catch {
      diagnostics.push({
        severity: 'error',
        code: 'invalid_front_matter',
        message: 'O front matter (bloco entre ---) não é YAML válido.',
      })
    }
    tree.children = tree.children.filter((c) => c.type !== 'yaml')
  }

  // Título: front matter ou o primeiro H1 (que sai do corpo para o cabeçalho)
  const h1s: Heading[] = []
  visit(tree, 'heading', (h: Heading) => {
    if (h.depth === 1) h1s.push(h)
  })
  if (h1s.length > 1) {
    diagnostics.push({
      severity: 'warning',
      code: 'duplicate_title',
      message:
        'Só o primeiro título de nível 1 vira o título do documento; os outros ficam no corpo.',
      line: h1s[1]?.position?.start.line,
    })
  }
  if (!meta.title && h1s[0]) meta.title = toString(h1s[0]).replace(/^\d+(\.\d+)?\.?\s*/, '')
  if (h1s[0]) tree.children = tree.children.filter((c) => c !== h1s[0])

  // Níveis de título pulados (## → ####)
  let previousDepth = 1
  visit(tree, 'heading', (h: Heading) => {
    if (h.depth > previousDepth + 1) {
      diagnostics.push({
        severity: 'warning',
        code: 'skipped_heading_level',
        message: `O título “${toString(h)}” pula de nível ${previousDepth} para ${h.depth}. Use ## para seção, ### para cláusula e #### para item da cláusula.`,
        line: h.position?.start.line,
      })
    }
    previousDepth = h.depth
  })

  // Os blocos presentes, pelo `data-block` que o remarkLegal gravou
  const present = new Set<string>()
  visit(tree, (node) => {
    const block = (node.data as { hProperties?: { 'data-block'?: string } } | undefined)
      ?.hProperties?.['data-block']
    if (block) present.add(block)
  })

  // `::anamnesis` só cabe onde a anamnese é base: anamnese, contrato e termo de consentimento (P-DOC-03)
  if (present.has('anamnesis') && !ANAMNESIS_DOCUMENT_TYPES.includes(meta.type)) {
    diagnostics.push({
      severity: 'warning',
      code: 'misplaced_directive',
      message: `O bloco ::anamnesis entra em anamnese, contrato ou termo de consentimento; este documento é do tipo ${DOCUMENT_TYPE_LABELS[
        meta.type
      ].toLowerCase()} (${meta.type}).`,
      block: 'anamnesis',
    })
  }

  // Blocos obrigatórios do termo de consentimento
  if (meta.type === 'consent_form') {
    for (const name of REQUIRED_CONSENT_FORM_BLOCKS) {
      if (!present.has(name)) {
        diagnostics.push({
          severity: 'error',
          code: 'missing_required_block',
          message: `Termo de consentimento precisa do bloco :::${name}. O paciente lê primeiro o resumo, o que autoriza e o que pode acontecer.`,
          block: name,
        })
      }
    }
  }

  // Variáveis
  const variables = extractVariables(markdown)
  if (options.catalog) {
    for (const v of variables) {
      const entry = getCatalogVariable(v)
      if (!entry) {
        const suggestion = suggestCatalogVariable(v)
        diagnostics.push({
          severity: 'error',
          code: 'invalid_variable',
          message: suggestion
            ? `A variável {{${v}}} não existe. Use {{${suggestion}}}.`
            : `A variável {{${v}}} não existe no catálogo. Escolha uma no menu Variável.`,
        })
      } else if (entry.type === 'block') {
        diagnostics.push({
          severity: 'error',
          code: 'invalid_variable',
          message: `{{${v}}} não entra no meio do texto: é um bloco inteiro do documento.`,
        })
      }
    }
  }
  // Marcadores do sistema anterior sem destino no dialeto (`{{?anamneseperguntaeresposta}}`): a conversão
  // os deixou marcados; o documento não pode sair com eles.
  for (const m of markdown.matchAll(/\{\{\s*\?\s*([\w.]+)\s*\}\}/g)) {
    diagnostics.push({
      severity: 'error',
      code: 'legacy_placeholder',
      message: `O marcador {{?${m[1]}}} veio do sistema anterior e não tem destino no dialeto. Troque pela variável ou pelo bloco certo antes de publicar.`,
      line: markdown.slice(0, m.index ?? 0).split('\n').length,
    })
  }

  if (options.variables) {
    for (const v of variables) {
      const value = options.variables[v]
      if (value === undefined || value === null) {
        diagnostics.push({
          severity: 'error',
          code: 'missing_variable_value',
          message: `A variável {{${v}}} não recebeu valor. O documento não pode sair com espaço em branco.`,
        })
      }
    }
  }

  return { meta, tree, diagnostics, variables }
}
